import { type FC, useState } from 'react';
import classNames from 'classnames';
import {
  Dropdown,
  IconButton,
  TextArea,
  TextInput,
  Toggle,
} from '@carbon/react';
import {
  CheckmarkFilled,
  CheckmarkOutline,
  Misuse,
  MisuseOutline,
} from '@carbon/react/icons';
import { ColorSwatch } from 'components/color-picker';
import styles from './create-area-panel.module.scss';
import type { AreaDetails, AreaVerification } from './create-area-types';

/** Each verdict's tooltip, off and on, as the story words them. */
const VERIFICATION_TOOLTIPS: Record<
  AreaVerification,
  { off: string; on: string }
> = {
  correct: {
    off: "Mark correct to block any automatic GIS updates. Updates are still available in workspaces but won't be automatically applied.",
    on: "Marked correct — automatic GIS updates are blocked. Updates are still available in workspaces but won't be automatically applied. Click to clear.",
  },
  incorrect: {
    off: 'Mark incorrect.',
    on: 'Marked incorrect. Click to clear',
  },
};

interface CreateAreaVerificationProps {
  verification: AreaVerification | null;
  onChange: (verification: AreaVerification | null) => void;
}

/**
 * "Verified" with Mark correct and Mark incorrect beside it, drawn as the design
 * system's Verification status: outline icons until one is on, then the filled
 * check in Blue 60 or the filled misuse in Red 60. One is on at a time, and
 * clicking the one that is on clears it.
 *
 * @param {CreateAreaVerificationProps} props - The choice and its setter.
 * @return {JSX.Element} The label and the two buttons.
 */
function CreateAreaVerification({
  verification,
  onChange,
}: CreateAreaVerificationProps): JSX.Element {
  /**
   * Turns a verdict on, or clears it when it is the one already on.
   *
   * @param {AreaVerification} next - The verdict clicked.
   * @return {void}
   */
  const toggle = (next: AreaVerification) =>
    onChange(verification === next ? null : next);
  /**
   * A button's tooltip for whether its verdict is on.
   *
   * @param {AreaVerification} kind - The button's verdict.
   * @return {string} The tooltip.
   */
  const tooltip = (kind: AreaVerification) =>
    VERIFICATION_TOOLTIPS[kind][verification === kind ? 'on' : 'off'];
  return (
    <div className={styles.verification}>
      <span>Verified</span>
      <IconButton
        kind="ghost"
        size="sm"
        align="top"
        label={tooltip('correct')}
        isSelected={verification === 'correct'}
        className={classNames({
          [styles.markedCorrect]: verification === 'correct',
        })}
        onClick={() => toggle('correct')}
      >
        {verification === 'correct' ? (
          <CheckmarkFilled />
        ) : (
          <CheckmarkOutline />
        )}
      </IconButton>
      <IconButton
        kind="ghost"
        size="sm"
        align="top-end"
        label={tooltip('incorrect')}
        isSelected={verification === 'incorrect'}
        className={classNames({
          [styles.markedIncorrect]: verification === 'incorrect',
        })}
        onClick={() => toggle('incorrect')}
      >
        {verification === 'incorrect' ? <Misuse /> : <MisuseOutline />}
      </IconButton>
    </div>
  );
}

interface CreateAreaDetailsProps {
  details: AreaDetails;
  onChange: (patch: Partial<AreaDetails>) => void;
  /** Units collect a Horizontal Unit flag instead of colour, purpose and shared. */
  isUnit: boolean;
}

/**
 * Area details fieldset. Units carry a reduced set — the unit record owns
 * colour, purpose and sharing — so those fields are swapped for the Horizontal
 * Unit flag.
 *
 * @param {CreateAreaDetailsProps} props - The component props.
 * @return {JSX.Element} The details fields.
 */
export const CreateAreaDetails: FC<CreateAreaDetailsProps> = ({
  details,
  onChange,
  isUnit,
}) => {
  // Create holds while the name is empty; once the field has been left empty the
  // reason shows on it, rather than the button just staying off.
  const [nameTouched, setNameTouched] = useState(false);
  const name = (
    <TextInput
      id="new-area-name"
      labelText="Area name"
      placeholder="e.g. Wolfcamp A Type Curve Area"
      value={details.name}
      invalid={nameTouched && details.name.trim() === ''}
      invalidText="Area name is required"
      onBlur={() => setNameTouched(true)}
      onChange={(event) => onChange({ name: event.target.value })}
    />
  );

  const horizontalUnit = (
    <div className={styles.field}>
      {/*
        The label rides in labelA/labelB rather than labelText so it renders
        inside Carbon's `__appearance` grid — switch first, then the text with
        the 8px gap the grid already provides. As `labelText` it lands in
        `__label-text`, which carries no margin and sits flush against the
        switch. `labelText` still has to be present but empty, or Carbon
        reserves space for a label it never draws.
      */}
      <Toggle
        id="new-area-horizontal-unit"
        size="sm"
        aria-label="Horizontal Unit"
        labelText=""
        labelA="Horizontal Unit"
        labelB="Horizontal Unit"
        toggled={details.horizontalUnit}
        onToggle={(checked: boolean) => onChange({ horizontalUnit: checked })}
      />
      <p className={styles.hint}>
        {details.horizontalUnit
          ? 'Laterals are developed horizontally across this unit.'
          : 'This unit is developed vertically.'}
      </p>
    </div>
  );

  const displayColor = (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>Display color</span>
      <div className={styles.colorRow}>
        <ColorSwatch
          color={details.displayColor}
          onChange={(color: string) => onChange({ displayColor: color })}
        />
        <span>{details.displayColor.toUpperCase()}</span>
        <CreateAreaVerification
          verification={details.verification}
          onChange={(verification) => onChange({ verification })}
        />
      </div>
    </div>
  );

  const purpose = (
    <TextInput
      id="new-area-purpose"
      labelText="Purpose"
      placeholder="Short description of what this area is used for"
      value={details.purpose}
      onChange={(event) => onChange({ purpose: event.target.value })}
    />
  );

  const sharedDropdown = (
    <Dropdown
      id="new-area-shared"
      titleText="Shared"
      label="No"
      items={['No', 'Yes']}
      selectedItem={details.shared ? 'Yes' : 'No'}
      onChange={({ selectedItem }: { selectedItem: string | null }) =>
        onChange({ shared: selectedItem === 'Yes' })
      }
    />
  );

  const notes = (
    <TextArea
      id="new-area-notes"
      labelText="Notes / lineage"
      placeholder="Optional context, source lineage, or review notes"
      rows={3}
      value={details.notes}
      onChange={(event) => onChange({ notes: event.target.value })}
    />
  );

  return (
    <section className={classNames(styles.section, styles.divided)}>
      <h4 className={styles.sectionTitle}>Area details</h4>
      {name}
      {isUnit ? (
        horizontalUnit
      ) : (
        <>
          {purpose}
          <div className={styles.field}>
            {sharedDropdown}
            <p className={styles.hint}>
              Sharing will add this layer to all Insights maps as a new layer.
            </p>
          </div>
        </>
      )}
      {notes}
      {!isUnit && displayColor}
    </section>
  );
};

export default CreateAreaDetails;
